import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Container, Pagination, Card, TextualLink, Button, StatusMessage, Message,
} from '@hvcgroep/design-system';
import { Helmet } from 'react-helmet';
import GeoThermie from '@hvcgroep/design-system/assets/illustrations/Geothermie.svg';
import { Link, useLocation } from 'react-router-dom';
import ContractSwitcherHeader from '../../containers/ContractSwitcherHeader';
import SkeletonLoader from '../../components/SkeletonLoader';
import usePaginatedMeterReads from '../../hooks/usePaginatedMeterReads';
import useContract from '../../hooks/useContract';
import MeterReadsTable from '../../components/MeterReadsTable';
import ErrorPage from '../ErrorPage';
import CustomButtonLinkWrapper from '../../components/CustomButtonLinkWrapper';

import './style.scss';

interface LocationState {
  submitStatus?: 'success' | 'error';
}

const Usage: React.FC = () => {
  const { t } = useTranslation();
  const heading = t('pages.usage.title');
  const siteTitle = t('general.siteTitle');
  const location = useLocation<LocationState>();
  const submitStatus = location.state?.submitStatus;

  const {
    selectedContract,
    isLoading: isContractLoading,
    isError,
  } = useContract();

  const {
    meterReads,
    pagination,
    isLoading: isMeterReadsLoading,
    isError: isMeterReadsError,
  } = usePaginatedMeterReads(selectedContract?.id);

  const [isVisible, setIsVisible] = useState(true);

  const showMessage = submitStatus && isVisible;

  const isLoading = isContractLoading || isMeterReadsLoading;

  if (isError) {
    return <ErrorPage type="serverError" />;
  }

  const hasTaskId = !!selectedContract?.taskId;

  return (
    <Container fluid>
      <Helmet><title>{`${heading} - ${siteTitle}`}</title></Helmet>

      {(meterReads.length === 0 && isLoading) && (<SkeletonLoader type="usage" />)}

      {(!isLoading) && (
      <>
        {showMessage && (
          <Message
            type={submitStatus === 'success' ? 'status' : 'error'}
            actionButton={<TextualLink onClick={() => setIsVisible(false)}>{t('messages.closeMessage')}</TextualLink>}
          >
            <p>{submitStatus === 'success' ? t('pages.submitMeterReadings.form.messages.success') : t('pages.submitMeterReadings.form.messages.error')}</p>
          </Message>
        )}

        <div className="usage__heading">
          <ContractSwitcherHeader heading={heading} />
          {hasTaskId && (
            <Button href="/meterstanden-doorgeven" variation="center" CustomLinkComponent={CustomButtonLinkWrapper}>
              {t('pages.usage.submitMeterReadings')}
            </Button>
          )}
        </div>
        <div className="usage__table-container">
          <MeterReadsTable meterReads={!isMeterReadsError ? meterReads : []} />
          {isMeterReadsError && (
            <StatusMessage type="error">
              {t('meterReadsTable.error')}
            </StatusMessage>
          )}
          {meterReads.length === 0 && !isMeterReadsError && (
            <StatusMessage type="info">
              {t('meterReadsTable.noMeterReadsFound')}
            </StatusMessage>
          )}
          {pagination.totalPages > 1 && !isMeterReadsError && (
            <Pagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              onPageClick={(page) => pagination.setCurrentPage(page)}
            />
          )}
        </div>

        <Card
          heading={t('pages.usage.yourConnection')}
          image={GeoThermie}
          actionButton={
    (
      <TextualLink asChild>
        <Link to="/mijn-gegevens">
          {t('pages.usage.viewContractDetails')}
        </Link>
      </TextualLink>
    )
  }
        >
          {t('pages.usage.text')}
        </Card>
      </>
      )}


    </Container>
  );
};

export default Usage;
